"use client";

import { useEffect } from "react";

import { StatusScreen } from "@/components/StatusScreen";
import { reportError } from "@/lib/report-error";

import "./globals.css";

/**
 * Last-resort error boundary. It replaces the root layout entirely, so it
 * renders its own <html>/<body> and sits ABOVE the next-intl provider — no
 * translation context (or locale-aware router) is available here. Copy is
 * therefore static English and the "home" action is a plain anchor — a full
 * document load rather than a next-intl <Link>, and a control that still works
 * when the crash broke hydration, which a button's onClick does not. Fonts fall back to the token stacks in globals.css
 * since the next/font variables lived on the now-discarded layout.
 */
/* `retry`, not `reset`. A 500 in production is almost always a SERVER
 * component throwing — a query that failed, a fetch that timed out — and
 * `reset()` only clears the error state and re-renders the tree the client
 * already has. There is no fresh payload to render, so the same failure comes
 * straight back and the button reads as doing nothing. `retry()` re-fetches
 * the segment first. Stable since Next 16.3. */
export default function GlobalError({
  error,
  retry,
}: {
  error: Error & { digest?: string };
  retry: () => void;
}) {
  useEffect(() => {
    reportError(error, { digest: error.digest, boundary: "global" });
  }, [error]);

  return (
    <html lang="en" className="h-full">
      <body className="min-h-full bg-bg text-fg">
        <StatusScreen
          code="500"
          title="Something went wrong."
          body="An unexpected error stopped the page from loading. Try again, or head back to the homepage."
          actions={
            <>
              <button
                type="button"
                onClick={() => retry()}
                className="inline-flex h-12 cursor-pointer items-center rounded-full bg-fg px-6 font-mono text-sm text-bg transition-opacity hover:opacity-80"
              >
                try again
              </button>
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- a full document load out of the crashed runtime; <Link> would client-navigate back into it */}
              <a
                href="/"
                className="px-3 py-1 font-mono text-sm text-fg underline-offset-4 hover:underline"
              >
                ⮐ back home
              </a>
            </>
          }
        />
      </body>
    </html>
  );
}
